import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
// import { ReactComponent as NavigateArrow } from '../../assets/icons/device-management/navigate-back.svg';
import AddRole from '../../pages/user-management/component/AddRole';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slice/NotificationManagementSlice';
import { fetchAllPermissions, fetchAllRoles } from '../../redux/slice/RoleManagementSlice';
import { changeDateFormat } from '../../shared-functions/DataTime';
import { stringValidation } from '../../shared-functions/Validation';
import roles from '../../utils/api/roles';
import MyButton from '../button/Button';
import CheckBoxButtons from '../check-box-button/CheckBoxButtons';
import CustomInput from '../custom-input/CustomInput';
import ModalBox from '../modal-box/ModalBox';

interface RoleSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  onEdit?: (id: string) => void;
}

interface RoleDataProperties {
  _id: string;
  id: string;
  name: string;
  responsibility: string;
  permissions: string[];
  createdAt?: string;
  users: {
    name: string;
    status: string;
    email: string;
    phoneNumber: string;
  }[];
}

interface RoleFormState {
  name: string;
  responsibility: string;
  permissions: string[];
}

/**
 * Role side panel
 * @param properties - The properties for the side panel
 * @returns {React.ReactElement} - The rendered side panel
 */
function RoleSidePanel(properties: RoleSidePanelProperties) {
  const { t } = useTranslation('userManagement');
  const { fetchIdFrom, id } = properties;

  const dispatch = useAppDispatch();
  const { permissionsList, page, limit } = useAppSelector((state) => state.roles);
  const [searchParameters, setSearchParameters] = useSearchParams();

  const [roleData, setRoleData] = useState<RoleDataProperties>();

  // Edit Mode State
  const [isEdit, setIsEdit] = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [roleDetails, setRoleDetails] = useState<RoleFormState>({
    name: '',
    responsibility: '',
    permissions: [],
  });
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const [modalView, setModalView] = useState({
    status: false,
    title: '',
    content: <div />,
    header: false,
    rightCorner: '',
    notification: false,
  });

  /**
   * Function to fetch role details
   * @param roleId
   */
  const handleFetchRoleDetails = async (roleId: string) => {
    roles
      .fetchRoleById(roleId)
      .then((response) => {
        setRoleData(response?.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || t('somethingWentWrong'),
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Toggle Edit Mode
   */
  const toggleEditMode = () => {
    if (isEdit) {
      setIsEdit(false);
      setShowFormErrors(false);
    } else if (roleData) {
      setRoleDetails({
        name: roleData.name,
        responsibility: roleData.responsibility,
        permissions: roleData.permissions,
      });
      setIsEdit(true);
    }
  };

  /**
   * Handle Input Change
   */
  const handleInputChange = (name: string, value: string | number | (string | number)[]) => {
    setRoleDetails((previous) => ({
      ...previous,
      [name]: value,
    }));
  };
  /**
   * Handle Edit Submit
   */
  const handleEditSubmit = () => {
    setShowFormErrors(true);
    if (!roleData) return;

    if (
      stringValidation(1, 100, roleDetails.name) &&
      stringValidation(1, 100, roleDetails.responsibility) &&
      roleDetails.permissions.length > 0
    ) {
      setFormLoading(true);
      const request = {
        name: roleDetails.name,
        responsibility: roleDetails.responsibility,
        permissions: roleDetails.permissions,
      };
      roles
        .editRoles(roleData._id, request)
        .then(() => {
          setFormLoading(false);
          setIsEdit(false);
          dispatch(
            setNotificationMessage({
              message: t('roleUpdatedSuccessfully') || 'Role updated successfully',
              status: true,
              type: 'success',
              code: 0,
            }),
          );
          // Refresh details and list
          handleFetchRoleDetails(roleData._id);
          dispatch(fetchAllRoles({ page, limit }));
        })
        .catch((error) => {
          setFormLoading(false);
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message,
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  useEffect(() => {
    let roleId;
    if (fetchIdFrom === 'queryParams') {
      roleId = searchParameters.get('roleId');
    } else if (fetchIdFrom === 'props') {
      roleId = id;
    }

    if (!roleId) {
      setRoleData(undefined);
      setIsEdit(false);
      setAddMode(false);
      return;
    }

    if (roleId === 'add-role') {
      setAddMode(true);
      setIsEdit(false);
      setRoleData(undefined);
      return;
    }

    handleFetchRoleDetails(roleId);
    setIsEdit(false); // Reset edit mode on ID change
    setAddMode(false);
  }, [searchParameters, id]);

  useEffect(() => {
    if (permissionsList.length === 0) {
      dispatch(fetchAllPermissions());
    }
  }, []);

  /**
   * Handle Close Panel
   */
  const handleClosePanel = () => {
    const parameters = new URLSearchParams(searchParameters);
    parameters.delete('roleId');
    setSearchParameters(parameters);
  };

  /**
   * Handle Complete Task
   */
  const handleCompleteTask = (message: string) => {
    dispatch(
      setNotificationMessage({
        message,
        status: true,
        type: 'success',
        code: 0,
      }),
    );
    dispatch(fetchAllRoles({ page, limit }));
    handleClosePanel();
  };

  if (addMode) {
    return (
      <div className="user-details-side-panel" style={{ height: '100%' }}>
        <AddRole closeModal={handleClosePanel} completedTask={handleCompleteTask} />
      </div>
    );
  }

  return (
    <div className="user-details-side-panel" style={{ height: '100%' }}>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setModalView((previous) => ({ ...previous, status: false }))}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />

      <div className="panel-header">
        <p className="header-label">{t('roleDetails')}</p>
        {!isEdit && (
          <div className="action-buttons">
            <EditIcon
              className="icon edit"
              onClick={toggleEditMode}
              style={{ cursor: 'pointer' }}
            />
          </div>
        )}
      </div>
      <div className="user-details-group">
        <div className="profile-card">
          {isEdit ? (
            // Edit Mode Fields
            <div className="edit-fields-container">
              <div className="info-row-input">
                <CustomInput
                  name="name"
                  inputLabel={t('roleName')}
                  type="text"
                  placeholder={t('enterName')}
                  value={roleDetails.name}
                  onChangeValue={handleInputChange}
                  valueRequired
                  error={showFormErrors && !stringValidation(1, 100, roleDetails.name)}
                  errorMessage={t('roleNameRequired') || 'Role name is required'}
                />
              </div>

              <div className="info-row-input">
                <CustomInput
                  name="responsibility"
                  inputLabel={t('responsibility')}
                  type="text"
                  placeholder={t('describeHere')}
                  value={roleDetails.responsibility}
                  onChangeValue={handleInputChange}
                  valueRequired
                  error={showFormErrors && !stringValidation(1, 100, roleDetails.responsibility)}
                  errorMessage={t('responsibilityRequired') || 'Responsibility is required'}
                />
              </div>

              <div className="info-row-input">
                <CheckBoxButtons
                  name="permissions"
                  list={permissionsList}
                  inputLabel={t('permissions')}
                  value={roleDetails.permissions}
                  onChangeValue={handleInputChange}
                  valueRequired
                  error={showFormErrors && roleDetails.permissions.length === 0}
                  errorMessage={t('permissionsRequired') || 'At least one permission is required'}
                />
              </div>
            </div>
          ) : (
            // View Mode Layout
            <div className="edit-fields-container">
              <div className="info-row-input" style={{ display: 'flex', gap: '15px' }}>
                <CustomInput
                  name="name"
                  inputLabel={t('roleName')}
                  type="text"
                  placeholder={t('enterName')}
                  value={roleData?.name || ''}
                  onChangeValue={() => {}}
                  valueRequired
                  readOnly
                  fontSize="small"
                />
                <CustomInput
                  name="createdAt"
                  inputLabel={t('createdAt')}
                  type="text"
                  placeholder=""
                  value={roleData?.createdAt ? changeDateFormat(roleData.createdAt) : '-'}
                  onChangeValue={() => {}}
                  valueRequired
                  readOnly
                  fontSize="small"
                />
              </div>

              <div className="info-row-input">
                <CustomInput
                  name="responsibility"
                  inputLabel={t('responsibility')}
                  type="text"
                  placeholder={t('describeHere')}
                  value={roleData?.responsibility || ''}
                  onChangeValue={() => {}}
                  valueRequired
                  readOnly
                />
              </div>

              <div className="info-row-input">
                <CheckBoxButtons
                  name="permissions"
                  list={permissionsList}
                  inputLabel={t('permissions')}
                  value={roleData?.permissions || []}
                  onChangeValue={() => {}}
                  valueRequired
                  showOnlySelected
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {isEdit && (
        <div className="footer-actions">
          <MyButton label={t('cancel')} buttonType="outline" onClickFunc={toggleEditMode} />
          <MyButton
            label={t('update')}
            buttonType="submit"
            onClickFunc={handleEditSubmit}
            disableButton={formLoading}
            loading={formLoading}
          />
        </div>
      )}
    </div>
  );
}

export default RoleSidePanel;
